<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="PatientVerificationList" label="Patient Verifier" :active="true" />
    </breadcrumb-list>
    <section class="container is-max-hd">
      <div class="box">
        <div class="level">
          <div class="level-left">
            <div class="level-item">
              <div class="title-with-details">
                <h1 class="title is-4">Patient Verifier</h1>
                <a
                  href="#"
                  @click.prevent="showPatientVerificationModal = true"
                  class="feature-details-link-title"
                >
                  <span class="details-text">See details</span>
                  <b-icon icon="magnify-plus" size="is-small" class="bounce-icon" />
                </a>
              </div>
            </div>
          </div>
          <div class="level-right">
            <div v-if="!isRestricted" class="level-item filter-select-item">
              <b-field expanded>
                <b-select
                  v-model="selectedUserID"
                  placeholder="Show Verifications for User"
                  aria-label="User"
                  :loading="loadingUsers"
                  expanded
                >
                  <option value="">All Users</option>
                  <option v-for="user in userOptions" :key="user.id" :value="user.id">
                    {{ user.first_name }} {{ user.last_name }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="level-item">
              <div class="subscription-wrapper">
                <b-button
                  type="is-primary"
                  :disabled="!canSubmit"
                  @click="canSubmit ? $router.push({ name: 'PatientVerificationCreate' }) : null"
                >
                  Create New Verification
                </b-button>
              </div>
            </div>
          </div>
        </div>
        <b-message v-if="error" type="is-danger" :closable="false">{{ error }}</b-message>
        <b-message v-else-if="loaded && !canSubmit" type="is-warning" :closable="false">
          {{
            subscription && subscription.is_active
              ? 'Your verification allowance has been reached. You can still view your history.'
              : 'An active or trialing subscription is required to submit verifications.'
          }}
          <span
            v-if="
              subscription &&
              subscription.is_active &&
              !subscription.cancel_at_period_end &&
              subscription.next_bill_date
            "
          >
            Your allowance resets on {{ subscription.next_bill_date | formatDate }}.
          </span>
          <router-link v-if="!hasActiveSubscription && isManager" :to="{ name: 'PlanList' }">
            View subscription plans
          </router-link>
          <span v-else-if="!hasActiveSubscription">
            Contact your organization manager to subscribe.
          </span>
        </b-message>
        <hr />
        <PatientVerificationTable :user-id="selectedUserID" />
      </div>
    </section>
    <b-modal v-model="showPatientVerificationModal" :width="640" scroll="keep">
      <PatientVerificationFeatureDetail />
    </b-modal>
  </div>
</template>
<script>
import request from '../request';
import { mapGetters } from 'vuex';
import PatientVerificationTable from '../components/PatientVerificationTable.vue';
import PatientVerificationFeatureDetail from '../components/PatientVerificationFeatureDetail.vue';
export default {
  name: 'PatientVerificationList',
  components: { PatientVerificationTable, PatientVerificationFeatureDetail },
  data() {
    return {
      subscription: null,
      loaded: false,
      error: '',
      selectedUserID: '',
      userOptions: [],
      loadingUsers: false,
      showPatientVerificationModal: false,
    };
  },
  methods: {
    async loadUsers() {
      if (this.isRestricted) return;
      this.loadingUsers = true;
      try {
        const resp = await request.get('/users/compact');
        this.userOptions = resp.data.users.sort((a, b) => a.first_name.localeCompare(b.first_name));
      } catch (err) {
        this.error = 'Unable to load user filters.';
      } finally {
        this.loadingUsers = false;
      }
    },
  },
  computed: {
    ...mapGetters(['hasActiveSubscription', 'isManager', 'isRestricted']),
    verificationLimit() {
      return this.subscription?.user_plans?.Base?.Plan?.patient_verifications || 0;
    },
    canSubmit() {
      return Boolean(
        this.subscription?.is_active &&
          this.subscription.patient_verification_used_quota < this.verificationLimit
      );
    },
  },
  async mounted() {
    this.loadUsers();
    if (!this.hasActiveSubscription) {
      this.loaded = true;
      return;
    }
    try {
      const resp = await request.get('/subscriptions/current');
      this.subscription = resp.data.subscription;
    } catch (err) {
      if (![403, 404].includes(err.response?.status))
        this.error = 'Unable to load verification allowance.';
    } finally {
      this.loaded = true;
    }
  },
};
</script>

<style scoped>
.title-with-details {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.title-with-details .title {
  margin-bottom: 0;
}
.feature-details-link-title {
  color: #7c3aed;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: color 0.2s ease;
  font-size: 0.875rem;
  text-decoration: none;
}
.feature-details-link-title:hover {
  color: #6d28d9;
  text-decoration: underline;
}
.details-text {
  font-weight: 500;
}
.bounce-icon {
  animation: bounce 2s infinite;
}
@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(-3px);
  }
}
/* Mobile responsive layout */
@media (max-width: 1023px) {
  .level {
    display: flex;
    flex-direction: column;
    align-items: stretch !important;
  }

  .level-left {
    display: none;
  }

  .level-right {
    display: flex;
    flex-wrap: wrap;
    width: 100% !important;
    gap: 0.75rem;
    margin-left: 0 !important;
    justify-content: stretch;
  }

  /* Create button - show first and full width */
  .level-right .level-item:last-child {
    order: -1;
    width: 100%;
    flex-basis: 100%;
    margin: 0 !important;
  }

  .level-right .level-item:last-child .subscription-wrapper {
    width: 100%;
  }

  .level-right .level-item:last-child .subscription-wrapper > .button {
    width: 100%;
  }

  /* Filter selects - side by side, each taking half width */
  .level-right .filter-select-item {
    flex: 1 1 calc(50% - 0.375rem);
    min-width: 0;
    margin: 0 !important;
    max-width: none;
  }

  .level-right .filter-select-item :deep(.field) {
    width: 100%;
    margin-bottom: 0;
  }

  .level-right .filter-select-item :deep(.control),
  .level-right .filter-select-item :deep(.select) {
    width: 100% !important;
    max-width: none !important;
  }

  .level-right .filter-select-item :deep(.select select) {
    width: 100% !important;
    max-width: none !important;
  }
}
</style>
